import React from 'react';
import { Card, Typography, Spin } from 'antd';
import { MaturidadeData } from '../../../../types/dashboard/fornecedores.types';

const { Title, Text } = Typography;

interface MaturidadeChartProps {
  data: MaturidadeData | null;
  loading?: boolean;
}

const MaturidadeChart: React.FC<MaturidadeChartProps> = ({ data, loading = false }) => {
  // Adicione logs para depuração
  console.log('MaturidadeChart - dados recebidos:', data);
  
  // Se os dados estiverem carregando ou forem nulos, mostre o spinner
  if (loading || !data) {
    return (
      <Card title={<Title level={4} style={{ margin: 0 }}>Maturidade Média</Title>} style={{ height: '100%' }}>
        <div style={{ height: 280, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  // Verificar se os dados têm a estrutura esperada
  if (typeof data.percentualMaturidade !== 'number' || !data.nivelConformidade) {
    console.warn('Dados de maturidade com formato incorreto:', data);
    return (
      <Card title={<Title level={4} style={{ margin: 0 }}>Maturidade média</Title>} style={{ height: '100%' }}>
        <div style={{ height: 280, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
          <Text type="warning">Erro ao processar dados de maturidade</Text>
        </div>
      </Card>
    );
  }

  // Determinar a altura do preenchimento do termômetro com base no percentual
  const getThermometerHeight = () => {
    // Garantir que o percentual seja um número válido entre 0 e 100
    const percentual = Math.max(0, Math.min(100, data.percentualMaturidade));
    return `${percentual}%`;
  };

  // Determinar a cor com base no nível de conformidade
  const getThermometerColor = () => {
    // Garantir que nivelConformidade seja um valor válido
    const nivel = data.nivelConformidade || 'medio';
    
    switch (nivel) {
      case 'alto':
        return '#7B39DB'; // Roxo
      case 'medio':
        return '#39DBDB'; // Azul Turquesa
        return '#52c41a'; // Verde
      case 'medio':
        return '#faad14'; // Amarelo
      case 'baixo':
        return '#ff4d4f'; // Vermelho
      default:
        console.warn('Nível de conformidade inválido:', nivel);
        return '#faad14'; // Amarelo como padrão
    }
  };

  // Determinar o gradiente baseado no nível de conformidade
  const getThermometerGradient = () => {
    const nivel = data.nivelConformidade || 'medio';
    
    switch (nivel) {
      case 'alto':
        return `linear-gradient(to top, #cf1322 0%, #faad14 50%, #389e0d 100%)`;
      case 'medio':
        return `linear-gradient(to top, #cf1322 0%, #faad14 50%, #ffc53d 100%)`;
      case 'baixo':
        return `linear-gradient(to top, #cf1322 0%, #ff4d4f 50%, #ff7875 100%)`;
      default:
        return `linear-gradient(to top, #cf1322 0%, #faad14 50%, #389e0d 100%)`;
    }
  };

  return (
    <Card
      title={<Title level={4} style={{ margin: 0 }}>Maturidade Média</Title>}
      style={{ height: '100%', border: '2px solid black' }}
    >
      <div style={{ display: 'flex', height: 280 }}>
        {/* Termômetro */}
        <div style={{ width: '40%', position: 'relative', display: 'flex', flexDirection: 'column' }}>
          {/* Escala do termômetro */}
          <div style={{ position: 'absolute', left: 20, top: 0, bottom: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Text>100%</Text>
            <Text>75%</Text>
            <Text>50%</Text>
            <Text>25%</Text>
            <Text>0%</Text>
          </div>
          
          {/* Termômetro base */}
          <div style={{ 
            width: 50, 
            height: '100%', 
            backgroundColor: '#f0f0f0', 
            borderRadius: 20,
            position: 'relative',
            overflow: 'visible', // Mudado para 'visible' para mostrar as setas
            margin: '0 auto'
          }}>
            {/* Fundo do termômetro com gradiente baseado no nível */}
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '100%',
              background: getThermometerGradient(),
              borderRadius: '20px 20px 20px 20px',
              opacity: 0.2
            }} />
            
            {/* Preenchimento do termômetro */}
            <div style={{ 
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: getThermometerHeight(),
              background: getThermometerGradient(),
              borderRadius: '0 0 20px 20px',
              transition: 'height 1s ease-in-out'
            }} />
            
            {/* Marcador com setas laterais pretas apontando para dentro */}
            <div style={{
              position: 'absolute',
              bottom: getThermometerHeight(),
              left: -15, // Aumentado para dar mais espaço
              right: -15, // Aumentado para dar mais espaço
              height: 6, // Aumentado para acomodar setas maiores
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 5 // Aumentado o z-index
            }}>
              {/* Seta esquerda apontando para dentro (direita) */}
              <div style={{
                width: 0,
                height: 0,
                borderTop: '8px solid transparent',
                borderBottom: '8px solid transparent',
                borderLeft: '12px solid #000000'
              }} />
              
              {/* Barra central */}
              <div style={{
                width: 50, // Largura fixa em vez de flex
                height: 3,
                backgroundColor: '#000000',
                margin: '0 2px'
              }} />
              
              {/* Seta direita apontando para dentro (esquerda) */}
              <div style={{
                width: 0,
                height: 0,
                borderTop: '8px solid transparent',
                borderBottom: '8px solid transparent',
                borderRight: '12px solid #000000'
              }} />
            </div>

            {/* Texto de percentual dentro do termômetro */}
            <div style={{
              position: 'absolute',
              bottom: `calc(${getThermometerHeight()} / 1 - 30px)`,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 4,
              padding: '4px 8px',
              whiteSpace: 'nowrap'
            }}>
              <Text style={{ 
                fontSize: 14, 
                fontWeight: 'bold', 
                color: 'white'
              }}>
                {data.percentualMaturidade}%
              </Text>
            </div>
          </div>
        </div>
        
        {/* Título e descrição maturidade média do cliente */}
        <div style={{ width: '50%', paddingLeft: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <Title level={4}>Maturidade Média por Cliente</Title>
            <Text>Média da pontuação de maturidade de todos os fornecedores avaliados</Text>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default MaturidadeChart;